import AddressBlock from '@app/components/AddressBlock'
import ContextReminderContainer from '@app/components/ContextReminder'
import CustomInput from '@app/components/CustomInput'
import DeleteConfirmationModal from '@app/components/DeleteConfirmationModal'
import { NavTabItem, NavTabs } from '@app/components/NavTabs'
import { ListingType } from '@app/models/enums'
import { ICurrentUserState } from '@app/store/modules/account'
import { IGlobalSiteSettingsState } from '@app/store/modules/global'
import { bindFunctions } from '@app/utils/class'
import { formatDate, formatListingSize } from '@app/utils/formatting'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import * as React from 'react'
import DocumentTitle from 'react-document-title'
import { Route, RouteComponentProps, Switch } from 'react-router-dom'
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Row,
  UncontrolledDropdown,
} from 'reactstrap'
import { actionCreators, IPipelineState } from '../modules/pipeline'
import PipelineActivity from './PipelineActivity'
import PipelineDates from './PipelineDates'
import PipelineDetails from './PipelineDetails'
import PipelineFiles from './PipelineFiles'
import PipelineProgress from './PipelineProgress'

export type PipelinePageProps = IPipelineState &
  typeof actionCreators &
  RouteComponentProps<{ id: string }> &
  ICurrentUserState &
  IGlobalSiteSettingsState

export class PipelinePage extends React.PureComponent<PipelinePageProps> {
  constructor(props: PipelinePageProps) {
    super(props)
    bindFunctions(this)
  }

  public componentDidMount() {
    this.props.loadPipeline(this.props.match.params.id)
  }

  public componentDidUpdate(prevProps: PipelinePageProps) {
    if (prevProps.match.params.id !== this.props.match.params.id) {
      this.props.loadPipeline(this.props.match.params.id)
    }
  }

  public render() {
    if (!this.props.pipeline.id) {
      return null
    }

    return (
      <DocumentTitle title={this.props.pipeline.name}>
        <Container className='my-3'>
          {this._renderHeader()}
          {this._renderTabs()}
          <Switch>
            <Route
              path={`${this.props.match.path}/details`}
              render={() => <PipelineDetails {...this.props} />}
            />
            <Route
              path={`${this.props.match.path}/dates`}
              render={() => <PipelineDates {...this.props} />}
            />
            <Route
              path={`${this.props.match.path}/files`}
              render={() => <PipelineFiles {...this.props} />}
            />
            <Route
              path={`${this.props.match.path}/progress`}
              render={() => <PipelineProgress {...this.props} />}
            />
            <Route
              path={`${this.props.match.path}/reminders`}
              render={() => (
                <ContextReminderContainer
                  contextId={this.props.pipeline.id}
                  {...this.props}
                />
              )}
            />
            <Route
              path={this.props.match.path}
              render={() => <PipelineActivity {...this.props} />}
            />
          </Switch>
          <DeleteConfirmationModal
            isOpen={this.props.showConfirmDelete}
            itemName={this.props.pipeline.name}
            onConfirm={() => this.props.deletePipeline(this.props.pipeline.id)}
            onToggleModal={() => this.props.toggleConfirmDelete(false)}
          />
        </Container>
      </DocumentTitle>
    )
  }

  private _renderHeader() {
    return (
      <Card>
        <CardHeader className='bg-secondary text-white d-flex justify-content-between'>
          <div>
            <FontAwesomeIcon icon={['fal', 'piggy-bank']} fixedWidth /> Pipeline
          </div>
          <div>
            <UncontrolledDropdown className='d-inline-block'>
              <DropdownToggle tag='a' className='px-1 text-center'>
                <FontAwesomeIcon icon={['fal', 'chevron-down']} fixedWidth />
              </DropdownToggle>
              <DropdownMenu right>
                <DropdownItem
                  onClick={() =>
                    this.props.history.push(
                      `/properties/property/${this.props.pipeline.associatedProperty.propertyId}`,
                    )
                  }
                >
                  Go to property
                </DropdownItem>
                <DropdownItem
                  onClick={() =>
                    this.props.history.push(
                      `/brokerage/listing/${this.props.pipeline.listing.id}`,
                    )
                  }
                >
                  Go to listing
                </DropdownItem>
                {(this.props.permissions.canEdit ||
                  this.props.permissions.canDelete) && <DropdownItem divider />}
                {this.props.permissions.canEdit && (
                  <DropdownItem
                    onClick={() =>
                      this.props.history.push(
                        `/brokerage/pipeline/${this.props.pipeline.id}/edit`,
                      )
                    }
                  >
                    Edit
                  </DropdownItem>
                )}
                {this.props.permissions.canDelete && (
                  <DropdownItem
                    disabled={this.props.isDeleting}
                    onClick={() => this.props.toggleConfirmDelete(true)}
                  >
                    Delete
                  </DropdownItem>
                )}
              </DropdownMenu>
            </UncontrolledDropdown>
          </div>
        </CardHeader>
        <CardBody>
          <Row className='mb-2'>
            <Col className='font-weight-bold' md={3} xl={2}>
              Name
            </Col>
            <Col>{this.props.pipeline.name}</Col>
          </Row>
          <Row className='mb-2'>
            <Col className='font-weight-bold' md={3} xl={2}>
              Property
            </Col>
            <Col>
              {this.props.pipeline.listing.property.name}
              <AddressBlock
                address={this.props.pipeline.listing.property.address}
              />
            </Col>
          </Row>
          {this.props.pipeline.listing.space && (
            <Row className='mb-2'>
              <Col className='font-weight-bold' md={3} xl={2}>
                Space
              </Col>
              <Col>{this.props.pipeline.listing.space.spaceNo}</Col>
            </Row>
          )}
          {this.props.pipeline.listing.lot && (
            <Row className='mb-2'>
              <Col className='font-weight-bold' md={3} xl={2}>
                Lot
              </Col>
              <Col>{this.props.pipeline.listing.lot.lotNo}</Col>
            </Row>
          )}
          <Row className='mb-2'>
            <Col className='font-weight-bold' md={3} xl={2}>
              Size
            </Col>
            <Col>{formatListingSize(this.props.pipeline.listing)}</Col>
          </Row>
          <Row className='mb-2'>
            <Col className='font-weight-bold' md={3} xl={2}>
              Effective Date
            </Col>
            <Col>
              {formatDate(
                this.props.pipeline.effectiveDateOfOffer,
                'MM/DD/YYYY',
                'Not set',
              )}
            </Col>
          </Row>
          <Row className='mb-2'>
            <Col className='font-weight-bold' md={3} xl={2}>
              Due Diligence Expiration Date
            </Col>
            <Col className='my-auto'>
              {formatDate(
                this.props.pipeline.dueDiligenceExpirationDate,
                'MM/DD/YYYY',
                'Not set',
              )}
            </Col>
          </Row>
          <Row className='mb-2'>
            <Col className='font-weight-bold' md={3} xl={2}>
              {this.props.pipeline.listing.type === ListingType.Lease
                ? 'Execution Date'
                : 'Closing Date'}
            </Col>
            <Col>
              {formatDate(
                this.props.pipeline.closingDate,
                'MM/DD/YYYY',
                'Not set',
              )}
            </Col>
          </Row>
          <Row>
            <Col className='font-weight-bold align-self-center' md={3} xl={2}>
              My Profit
            </Col>
            <Col md={2} xl={2}>
              <CustomInput
                style={{ width: '80%' }}
                type='number'
                value={this.props.profit.estimatedProfit}
                min='0'
                onChange={(e) => {
                  this.props.updateProfit(e)
                }}
              />
            </Col>
            <Col className='font-weight-bold align-self-center' md={3} xl={2}>
              My Probability
            </Col>
            <Col md={2} xl={2}>
              <CustomInput
                style={{ width: '80%' }}
                type='number'
                value={this.props.profit.completionProbability}
                min='0'
                max='100'
                onChange={(e) => {
                  this.props.updateProbability(e)
                }}
              />
            </Col>
          </Row>
        </CardBody>
      </Card>
    )
  }

  private _renderTabs() {
    return (
      <div className='my-3'>
        <NavTabs>
          <NavTabItem
            exact
            iconName='chart-line'
            label='Activity'
            to={this.props.match.url}
          />
          <NavTabItem
            iconName='list'
            label='Details'
            to={`${this.props.match.url}/details`}
          />
          <NavTabItem
            iconName='clock'
            label='Dates'
            to={`${this.props.match.url}/dates`}
          />
          <NavTabItem
            iconName='paperclip'
            label='Files'
            badgeCount={
              this.props.pipeline.listing.photos.size +
              this.props.pipeline.listing.property.attachments.size
            }
            to={`${this.props.match.url}/files`}
          />
          <NavTabItem
            badgeCount={this.props.pipeline.progress.percentageComplete}
            badgeSuffix='%'
            iconName='clipboard-list'
            label='Progress'
            to={`${this.props.match.url}/progress`}
          />
          <NavTabItem
            iconName='bell'
            label='Reminders'
            to={`${this.props.match.url}/reminders`}
          />
        </NavTabs>
      </div>
    )
  }
}
